import type { AuthTokensDto, AuthUserDto, ComplianceBlockDto } from '@runnerboy/types';

export type ClientApp = 'CONSUMER' | 'STORE' | 'ADMIN' | 'RUNNER';

export interface RefreshTokenStore {
  get(): Promise<string | null>;
  set(token: string | null): Promise<void>;
}

export interface ClientOptions {
  /** API origin, e.g. '' (same-origin web apps) or 'https://api.example.in' (native app). */
  baseUrl: string;
  app: ClientApp;
  /** Native apps keep the refresh token in secure storage; web apps rely on the httpOnly cookie. */
  refreshStore?: RefreshTokenStore;
  onSessionChange?: (user: AuthUserDto | null) => void;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    readonly details?: unknown,
    readonly complianceBlocks?: ComplianceBlockDto[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
  get isNetwork() {
    return this.status === 0;
  }
}

export function newIdempotencyKey(): string {
  const r = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return r.replace(/[^A-Za-z0-9-]/g, '');
}

export class RunnerBoyClient {
  accessToken: string | null = null;
  user: AuthUserDto | null = null;
  private refreshing: Promise<boolean> | null = null;
  private readonly f: typeof fetch;

  /** Native apps (refresh token in app storage) identify themselves so the API returns tokens in the body. */
  private get nativeHeader(): Record<string, string> {
    return this.opts.refreshStore ? { 'x-rb-client': 'native' } : {};
  }

  constructor(readonly opts: ClientOptions) {
    this.f = opts.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  }

  get apiBase() {
    return `${this.opts.baseUrl.replace(/\/$/, '')}/api/v1`;
  }

  setSession(tokens: Pick<AuthTokensDto, 'accessToken' | 'user'> & { refreshToken?: string }) {
    this.accessToken = tokens.accessToken;
    this.user = tokens.user;
    if (tokens.refreshToken && this.opts.refreshStore) void this.opts.refreshStore.set(tokens.refreshToken);
    this.opts.onSessionChange?.(tokens.user);
  }

  clearSession() {
    this.accessToken = null;
    this.user = null;
    if (this.opts.refreshStore) void this.opts.refreshStore.set(null);
    this.opts.onSessionChange?.(null);
  }

  /** Attempts to restore a session using the refresh token (cookie or secure store). */
  async restore(): Promise<AuthUserDto | null> {
    const ok = await this.refresh();
    return ok ? this.user : null;
  }

  private async refresh(): Promise<boolean> {
    if (this.refreshing) return this.refreshing;
    this.refreshing = (async () => {
      try {
        const stored = this.opts.refreshStore ? await this.opts.refreshStore.get() : undefined;
        if (this.opts.refreshStore && !stored) return false;
        const res = await this.f(`${this.apiBase}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json', 'x-requested-with': 'runnerboy', ...this.nativeHeader },
          body: JSON.stringify({ app: this.opts.app, ...(stored ? { refreshToken: stored } : {}) }),
        });
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) this.clearSession();
          return false;
        }
        this.setSession((await res.json()) as AuthTokensDto & { refreshToken?: string });
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => (this.refreshing = null), 0);
      }
    })();
    return this.refreshing;
  }

  async request<T>(method: string, path: string, body?: unknown, opts: { idempotencyKey?: string; signal?: AbortSignal; retryOnAuth?: boolean } = {}): Promise<T> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.opts.timeoutMs ?? 20_000);
    opts.signal?.addEventListener('abort', () => controller.abort());
    let res: Response;
    try {
      res = await this.f(`${this.apiBase}${path}`, {
        method,
        credentials: 'include',
        signal: controller.signal,
        headers: {
          ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
          ...(this.accessToken ? { authorization: `Bearer ${this.accessToken}` } : {}),
          ...(opts.idempotencyKey ? { 'idempotency-key': opts.idempotencyKey } : {}),
          'x-requested-with': 'runnerboy',
          ...this.nativeHeader,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      throw new ApiError(0, 'NETWORK', (err as Error).name === 'AbortError' ? 'Request timed out' : 'You appear to be offline');
    } finally {
      clearTimeout(timer);
    }
    if (res.status === 401 && opts.retryOnAuth !== false && !path.startsWith('/auth/')) {
      if (await this.refresh()) return this.request<T>(method, path, body, { ...opts, retryOnAuth: false });
    }
    const text = await res.text();
    const data = text ? safeParse(text) : null;
    if (!res.ok) {
      const e = (data as { error?: { code?: string; message?: string; requestId?: string; details?: unknown; complianceBlocks?: ComplianceBlockDto[] } } | null)?.error;
      throw new ApiError(res.status, e?.code ?? 'HTTP_ERROR', e?.message ?? `Request failed (${res.status})`, e?.requestId, e?.details, e?.complianceBlocks);
    }
    return data as T;
  }

  get<T>(path: string, signal?: AbortSignal) {
    return this.request<T>('GET', path, undefined, { signal });
  }
  post<T>(path: string, body?: unknown, opts?: { idempotencyKey?: string }) {
    return this.request<T>('POST', path, body ?? {}, opts);
  }
  put<T>(path: string, body?: unknown) {
    return this.request<T>('PUT', path, body ?? {});
  }
  patch<T>(path: string, body?: unknown) {
    return this.request<T>('PATCH', path, body ?? {});
  }
  del<T>(path: string) {
    return this.request<T>('DELETE', path);
  }

  // ---- Auth helpers --------------------------------------------------------------------------------------
  requestOtp(phone: string) {
    return this.post<{ sent: boolean; resendAfterSec: number; testMode?: boolean }>('/auth/otp/request', { phone, app: this.opts.app });
  }
  async verifyOtp(phone: string, code: string, deviceLabel?: string) {
    const tokens = await this.post<AuthTokensDto & { refreshToken?: string }>('/auth/otp/verify', { phone, code, app: this.opts.app, deviceLabel });
    this.setSession(tokens);
    return tokens.user;
  }
  async logout() {
    try {
      await this.post('/auth/logout');
    } finally {
      this.clearSession();
    }
  }
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.complianceBlocks?.[0]?.message ?? err.message;
  return 'Something went wrong. Please try again.';
}
